// What shows on the home screen (2026-10-22). Every feature stays available to
// every business; this only decides which shortcuts the Manage menu and the
// Screens menu show, and which checkout steps are on. Shared by onboarding,
// Settings > Home screen and the Manage menu's Customize mode, so all three
// agree on the list and on what each type of place starts with.

// Type of place, asked in onboarding step 2. Food kinds store
// business_type 'food_beverage' (same value as before, for the admin panel)
// and the kind itself as business_industry.
export const PLACE_TYPES = [
  { id: 'cafe',       label: 'Coffee shop / café', hint: 'Coffee, tea, light food' },
  { id: 'restaurant', label: 'Restaurant',         hint: 'Sit-down meals' },
  { id: 'bakery',     label: 'Bakery / dessert',   hint: 'Bread, pastries, sweets' },
  { id: 'food_truck', label: 'Food truck / stall', hint: 'Order and go' },
  { id: 'bar',        label: 'Bar / juice bar',    hint: 'Drinks first' },
  { id: 'other',      label: 'Something else',     hint: 'Shops, services, more' },
];

export const placeLabel = (id) => PLACE_TYPES.find((p) => p.id === id)?.label ?? null;

// "Something else" asks what they sell; the answer is the business_type.
export const OTHER_KINDS = [
  { id: 'retail',  label: 'A shop (products)' },
  { id: 'service', label: 'Services (salon, repair, classes)' },
  { id: 'other',   label: 'Something else' },
];

// Shortcuts that can be hidden. `group` is the menu they live in; `pro` only
// labels the row. Catalogue and Help are never hideable (the first-sale
// tutorial needs Catalogue, and Help must always be reachable).
export const HOME_ITEMS = [
  { key: 'tables',     group: 'manage',  label: 'Tables' },
  { key: 'receipt',    group: 'manage',  label: 'Receipt & Tax' },
  { key: 'promos',     group: 'manage',  label: 'Promos' },
  { key: 'inventory',  group: 'manage',  label: 'Inventory',  pro: true },
  { key: 'staff',      group: 'manage',  label: 'Staff' },
  { key: 'timesheets', group: 'manage',  label: 'Timesheets', pro: true },
  { key: 'schedule',   group: 'manage',  label: 'Schedule' },
  { key: 'timeclock',  group: 'manage',  label: 'Time Clock' },
  { key: 'reports',    group: 'manage',  label: 'Reports',    pro: true },
  { key: 'loyalty',    group: 'manage',  label: 'Loyalty',    pro: true },
  { key: 'kds',        group: 'screens', label: 'Queue Screen',    hint: 'Orders for the kitchen' },
  { key: 'cfd',        group: 'screens', label: 'Customer Screen', hint: 'Faces the customer at the counter' },
  { key: 'pickup',     group: 'screens', label: 'Pickup Screen',   hint: '"Your order is ready" board' },
];

export const TABLE_SERVICE = ['off', 'numbers', 'full'];

// The suggested setup for each type of place. `hidden` lists HOME_ITEMS keys
// left off the menus. The Tables shortcut also needs table service on.
const SUGGESTED = {
  cafe:       { orderType: true,  tips: true,  tables: 'numbers', waitlist: false, reservations: false, hidden: ['inventory'] },
  restaurant: { orderType: true,  tips: true,  tables: 'full',    waitlist: true,  reservations: true,  hidden: ['pickup'] },
  bakery:     { orderType: true,  tips: true,  tables: 'off',     waitlist: false, reservations: false, hidden: [] },
  food_truck: { orderType: false, tips: true,  tables: 'off',     waitlist: false, reservations: false, hidden: ['inventory'] },
  bar:        { orderType: true,  tips: true,  tables: 'numbers', waitlist: false, reservations: false, hidden: ['inventory'] },
  other:      { orderType: false, tips: false, tables: 'off',     waitlist: false, reservations: false, hidden: ['kds', 'pickup'] },
};

// Settings values for a type of place, keyed like state.settings.
export const suggestedSetup = (placeType) => {
  const s = SUGGESTED[placeType] ?? SUGGESTED.other;
  return {
    orderTypeEnabled:    s.orderType,
    tipsEnabled:         s.tips,
    tableService:        s.tables,
    waitlistEnabled:     s.waitlist,
    reservationsEnabled: s.reservations,
    homeHidden:          [...s.hidden],
  };
};

// Clean a stored hidden list: known keys only, no repeats.
export const cleanHidden = (list) => {
  const known = new Set(HOME_ITEMS.map((i) => i.key));
  return [...new Set((Array.isArray(list) ? list : []).filter((k) => known.has(k)))];
};
